import React from 'react';
import { graphql, compose } from 'react-apollo';
import { branch, renderComponent, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import LoadingPage from 'components/common/Loader/Spinner/Spinner';
import { Paper, Link, makeStyles } from '@material-ui/core';
import '../Home/Home.scss';
import { useViewport } from 'utils/getViewport/getViewport';
import { isFetchingAssessments, getAssessments } from 'selectors/assessments/selector';
import { listAssessments } from 'graphql/operations';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';
import MobileAssessmentsTable from 'components/threepp/home/MobileAssessmentsTable/MobileAssessmentsTable';
import { ControlledSection } from 'components/common/ControlledSection/ControlledSection';
import { EngageGrid } from 'components/common/EngageGrid/EngageGrid';
import { AssessmentProgressBar } from 'components/threepp/AssessmentProgressBar/AssessmentProgressBar';
import Tooltip from 'components/common/Tooltip/Tooltip';
import { getFormattedDueDate, getFormattedSubmitDate, formatOwners } from 'pages/utils/page-utils';
import { handleNameLinkSort, handleProgressSort, handleDueSort } from 'components/common/EngageGrid/helpers/customSortHelpers';

const useStyles = makeStyles(() => ({
  gridContainer: { padding: '1rem', '& +$gridContainer': { marginTop: '1rem' } },
  assessmentLink: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', color: 'rgba(0, 0, 0, 0.87)' }
}));

export const CustomerRequestsBase = props => {
  const { translate, assessments } = props;
  const { gridContainer, assessmentLink } = useStyles();
  const { isMobile } = useViewport();

  // Filter Assessments
  const customerAssessments = assessments?.filter(assessment => !assessment.productIdentifier || assessment.productIdentifier === 'E4V');
  const openAssessments = customerAssessments?.filter(a => a.status === 'NEW' || a.status === 'INCOMPLETE') || [];
  const submittedAssessments =
    customerAssessments?.filter(a => ['SUBMITTED', 'SUBMITTED_SYNC_SUCCESS', 'SUBMITTED_SYNC_FAILED'].includes(a.status)) || [];

  // EngageGrid Props
  const openAssessmentGridProps = React.useMemo(
    () => ({
      translate,
      columns: [
        { name: 'name', title: translate('model.assessment.name') },
        { name: 'due', title: translate('model.assessment.dueDate') },
        { name: 'company', title: translate('model.assessment.company') },
        { name: 'owner', title: translate('model.assessment.owners') },
        { name: 'progress', title: translate('model.assessment.progress') }
      ],
      rows: openAssessments.map(assessment => {
        const { name, id, dueDate, company, owners, metadata } = assessment;
        const { answeredFields, totalFields } = metadata;
        return {
          name: (
            <Tooltip title={name}>
              <Link href={`assessments/${id}`} className={assessmentLink}>
                {name}
              </Link>
            </Tooltip>
          ),
          due: getFormattedDueDate(translate, dueDate),
          owner: formatOwners(owners),
          company,
          progress: <AssessmentProgressBar className="col-completed" answered={answeredFields} total={totalFields} />
        };
      }),
      isFilteringEnabled: true,
      isSortingEnabled: true,
      defaultSorting: [{ columnName: 'due', direction: 'asc' }],
      columnSortingFnOverrides: [
        { columnName: 'name', compare: handleNameLinkSort },
        { columnName: 'due', compare: handleDueSort },
        { columnName: 'progress', compare: handleProgressSort }
      ],
      columnFilteringState: [
        {
          columnName: 'name',
          filteringEnabled: false
        },
        {
          columnName: 'due',
          filteringEnabled: false
        },
        {
          columnName: 'progress',
          filteringEnabled: false
        }
      ]
    }),
    [translate, openAssessments, assessmentLink]
  );

  const submittedAssessmentGridProps = React.useMemo(
    () => ({
      translate,
      columns: [
        { name: 'name', title: translate('model.assessment.name') },
        { name: 'submitted', title: translate('model.assessment.submitted') },
        { name: 'company', title: translate('model.assessment.company') },
        { name: 'owner', title: translate('model.assessment.owners') }
      ],
      rows: submittedAssessments.map(assessment => {
        const { name, id, company, owners, submitDate } = assessment;
        return {
          name: (
            <Tooltip title={name}>
              <Link href={`assessments/${id}`} className={assessmentLink}>
                {name}
              </Link>
            </Tooltip>
          ),
          owner: formatOwners(owners),
          company,
          submitted: getFormattedSubmitDate(translate, submitDate, true)
        };
      }),
      isFilteringEnabled: true,
      isSortingEnabled: true,
      defaultSorting: [{ columnName: 'submitted', direction: 'desc' }],
      columnSortingFnOverrides: [{ columnName: 'name', compare: handleNameLinkSort }]
    }),
    [assessmentLink, submittedAssessments, translate]
  );

  return (
    <div className={isMobile ? 'home-page mobile-home-page' : 'row home-page '}>
      <div className={isMobile ? 'mobile-home-paper' : 'col-md-9 main-page-content'}>
        <h5 className="paper-title">{translate('customerRequests.title')}</h5>

        <Paper className={gridContainer} elevation={3}>
          <ControlledSection name={`${translate('home.openAssessments')} (${openAssessments.length})`}>
            {!isMobile ? <EngageGrid {...openAssessmentGridProps} /> : <MobileAssessmentsTable assessments={openAssessments} />}
          </ControlledSection>
        </Paper>

        {submittedAssessments.length > 0 && (
          <Paper className={gridContainer} elevation={3}>
            <ControlledSection name={`${translate('home.submittedAssessments')} (${submittedAssessments.length})`} expanded={false}>
              {!isMobile ? <EngageGrid {...submittedAssessmentGridProps} /> : <MobileAssessmentsTable assessments={submittedAssessments} />}
            </ControlledSection>
          </Paper>
        )}
      </div>
    </div>
  );
};

export const CustomerRequestsHOC = compose(
  withRouter,
  withTranslation(),
  graphql(listAssessments, { options: { fetchPolicy: NETWORK_ONLY } }),
  mapProps(props => ({
    translate: props.t,
    history: props.history,
    listAssessments: props.listAssessments,
    isLoading: isFetchingAssessments(props),
    assessments: getAssessments(props)
  })),
  branch(
    ({ isLoading }) => isLoading,
    renderComponent(() => <LoadingPage size="lg" expanded />)
  )
);

export const CustomerRequests = CustomerRequestsHOC(CustomerRequestsBase);
